import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "popup", "existingList", "hunterList", "hunterLoading", "hunterEmpty", "hunterBtn",
    "addPersonPanel", "searchPanel", "searchInput", "searchResults", "searchLoading",
    "newPersonPanel", "newFirstName", "newLastName", "newEmail", "newRole",
    "newPersonError", "duplicateAlert"
  ]
  static values = { resultId: Number, domain: String, open: Boolean }

  connect() {
    this._onScroll = () => { if (this.openValue) this.positionPopup() }
    this._onCloseAll = (e) => { if (e.detail !== this && this.openValue) this.close() }
    window.addEventListener("scroll", this._onScroll, true)
    window.addEventListener("resize", this._onScroll)
    window.addEventListener("people-popup:close-all", this._onCloseAll)
  }

  disconnect() {
    window.removeEventListener("scroll", this._onScroll, true)
    window.removeEventListener("resize", this._onScroll)
    window.removeEventListener("people-popup:close-all", this._onCloseAll)
  }

  toggle(event) {
    event.stopPropagation()
    if (this.popupTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    // Close any other open popups first
    window.dispatchEvent(new CustomEvent("people-popup:close-all", { detail: this }))
    this.popupTarget.classList.remove("hidden")
    this.openValue = true
    this.positionPopup()
    this.fetchExisting()
  }

  positionPopup() {
    const btn = this.element.querySelector("button")
    const popup = this.popupTarget
    const rect = btn.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    // Use fixed positioning to escape overflow-hidden containers
    popup.style.position = "fixed"
    popup.style.right = `${window.innerWidth - rect.right}px`
    popup.style.width = "320px"
    popup.style.zIndex = "50"

    if (spaceBelow >= 300 || spaceBelow >= spaceAbove) {
      // Position below the button
      popup.style.top = `${rect.bottom + 4}px`
      popup.style.bottom = "auto"
    } else {
      // Position above the button
      popup.style.bottom = `${window.innerHeight - rect.top + 4}px`
      popup.style.top = "auto"
    }
  }

  close() {
    this.popupTarget.classList.add("hidden")
    this.popupTarget.removeAttribute("style")
    this.openValue = false
    // Reset add-person panels when closing
    if (this.hasAddPersonPanelTarget) this.addPersonPanelTarget.classList.add("hidden")
    if (this.hasSearchPanelTarget) this.searchPanelTarget.classList.add("hidden")
    if (this.hasNewPersonPanelTarget) this.newPersonPanelTarget.classList.add("hidden")
  }

  closeOnOutside(event) {
    if (this.openValue && !this.element.contains(event.target)) {
      this.close()
    }
  }

  async fetchExisting() {
    try {
      const response = await fetch(
        `/pr/discovery_results/${this.resultIdValue}/existing_contacts`,
        { headers: { "Accept": "application/json" } }
      )
      const data = await response.json()

      if (data.contacts && data.contacts.length > 0) {
        this.existingListTarget.innerHTML = data.contacts.map(c => `
          <div class="flex items-center justify-between py-1.5 px-2 text-sm">
            <div class="min-w-0 flex items-center gap-1.5">
              ${this.emailStatusIcon(c.email_status, c.pending_candidates)}
              <span class="font-medium text-[#2D2D2D]">${this.esc(c.name)}</span>
              ${c.role ? `<span class="text-[#8C8C8C] text-xs ml-1">${this.esc(c.role)}</span>` : ''}
            </div>
            <span class="text-xs text-[#8C8C8C] truncate ml-2">${this.esc(c.email || 'No email')}</span>
          </div>
        `).join('')
      } else {
        this.existingListTarget.innerHTML = '<p class="text-xs text-[#8C8C8C] py-2 px-2">No contacts in PRT yet.</p>'
      }
    } catch (e) {
      this.existingListTarget.innerHTML = '<p class="text-xs text-[#8C8C8C] py-2 px-2">Could not load contacts.</p>'
    }
  }

  async fetchHunter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.hunterBtnTarget.classList.add("hidden")
    this.hunterLoadingTarget.classList.remove("hidden")
    this.hunterListTarget.innerHTML = ""
    if (this.hasHunterEmptyTarget) this.hunterEmptyTarget.classList.add("hidden")

    try {
      const response = await fetch(
        `/pr/discovery_results/${this.resultIdValue}/hunter_contacts?domain=${encodeURIComponent(this.domainValue)}`,
        { headers: { "Accept": "application/json" } }
      )
      const data = await response.json()
      this.hunterLoadingTarget.classList.add("hidden")

      if (data.contacts && data.contacts.length > 0) {
        this.hunterListTarget.innerHTML = data.contacts.map((c, i) => `
          <label class="flex items-center gap-2 py-1.5 px-2 hover:bg-[#F5F3F0] rounded cursor-pointer text-sm">
            <input type="checkbox" name="hunter_contacts[${i}][selected]" value="1"
                   data-email="${this.esc(c.email)}" data-first="${this.esc(c.first_name || '')}" data-last="${this.esc(c.last_name || '')}" data-position="${this.esc(c.position || '')}"
                   class="rounded border-[#E8E4DF] text-[#C4A484] focus:ring-[#C4A484]">
            <div class="min-w-0 flex-1">
              <span class="font-medium text-[#2D2D2D]">${this.esc(c.first_name || '')} ${this.esc(c.last_name || '')}</span>
              ${c.position ? `<span class="text-[#8C8C8C] text-xs ml-1">${this.esc(c.position)}</span>` : ''}
              <span class="text-xs text-[#8C8C8C] block">${this.esc(c.email)}</span>
            </div>
            <span class="text-xs text-[#8C8C8C]">${c.confidence}%</span>
          </label>
        `).join('')
      } else {
        if (this.hasHunterEmptyTarget) this.hunterEmptyTarget.classList.remove("hidden")
      }
    } catch (e) {
      this.hunterLoadingTarget.classList.add("hidden")
      if (this.hasHunterEmptyTarget) this.hunterEmptyTarget.classList.remove("hidden")
    }
  }

  // Toggle the add-person options panel
  toggleAddPerson(event) {
    event.preventDefault()
    event.stopPropagation()
    this.addPersonPanelTarget.classList.toggle("hidden")
    this.searchPanelTarget.classList.add("hidden")
    this.newPersonPanelTarget.classList.add("hidden")
  }

  // Show the "Search PRT Database" panel
  showSearchPanel(event) {
    event.preventDefault()
    event.stopPropagation()
    this.searchPanelTarget.classList.remove("hidden")
    this.newPersonPanelTarget.classList.add("hidden")
    this.searchInputTarget.value = ""
    this.searchResultsTarget.innerHTML = ""
    this.searchInputTarget.focus()
  }

  // Show the "Add New Person" form
  showNewPersonPanel(event) {
    event.preventDefault()
    event.stopPropagation()
    this.newPersonPanelTarget.classList.remove("hidden")
    this.searchPanelTarget.classList.add("hidden")
    this.clearNewPersonForm()
    this.newFirstNameTarget.focus()
  }

  // Debounced search as user types
  searchContacts() {
    const query = this.searchInputTarget.value.trim()
    if (query.length < 2) {
      this.searchResultsTarget.innerHTML = ""
      return
    }

    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(async () => {
      if (this.hasSearchLoadingTarget) this.searchLoadingTarget.classList.remove("hidden")
      try {
        const response = await fetch(
          `/pr/discovery_results/${this.resultIdValue}/search_contacts?q=${encodeURIComponent(query)}`,
          { headers: { "Accept": "application/json" } }
        )
        const data = await response.json()
        if (this.hasSearchLoadingTarget) this.searchLoadingTarget.classList.add("hidden")

        if (data.contacts && data.contacts.length > 0) {
          this.searchResultsTarget.innerHTML = data.contacts.map(c => `
            <button type="button" data-action="click->people-popup#linkContact"
                    data-contact-id="${c.id}"
                    class="w-full flex items-center justify-between py-1.5 px-2 hover:bg-[#F5F3F0] rounded cursor-pointer text-sm text-left">
              <div class="min-w-0">
                <span class="font-medium text-[#2D2D2D]">${this.esc(c.name)}</span>
                ${c.outlet ? `<span class="text-[10px] text-[#8C8C8C] ml-1">${this.esc(c.outlet)}</span>` : ''}
              </div>
              <span class="text-xs text-[#8C8C8C] truncate ml-2">${this.esc(c.email)}</span>
            </button>
          `).join('')
        } else {
          this.searchResultsTarget.innerHTML = '<p class="text-xs text-[#8C8C8C] py-2 px-2">No matching contacts.</p>'
        }
      } catch (e) {
        if (this.hasSearchLoadingTarget) this.searchLoadingTarget.classList.add("hidden")
        this.searchResultsTarget.innerHTML = '<p class="text-xs text-[#E57373] py-2 px-2">Search failed.</p>'
      }
    }, 300)
  }

  // Link an existing contact (called when clicking a search result or "Link instead")
  async linkContact(event) {
    event.preventDefault()
    event.stopPropagation()
    const contactId = event.currentTarget.dataset.contactId
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(
        `/pr/discovery_results/${this.resultIdValue}/link_contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({ contact_id: contactId })
        }
      )
      const data = await response.json()

      if (data.status === "ok") {
        this.fetchExisting()
        this.addPersonPanelTarget.classList.add("hidden")
        this.searchPanelTarget.classList.add("hidden")
        this.newPersonPanelTarget.classList.add("hidden")
      } else {
        alert(data.message || "Failed to link contact.")
      }
    } catch (e) {
      alert("Failed to link contact.")
    }
  }

  // Submit the new person form
  async submitNewPerson(event) {
    event.preventDefault()
    event.stopPropagation()
    this.clearMessages()

    const email = this.newEmailTarget.value.trim()
    if (!email) {
      this.showError("Email is required.")
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const payload = {
      first_name: this.newFirstNameTarget.value.trim(),
      last_name: this.newLastNameTarget.value.trim(),
      email: email,
      role: this.newRoleTarget.value
    }

    try {
      const response = await fetch(
        `/pr/discovery_results/${this.resultIdValue}/create_contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify(payload)
        }
      )
      const data = await response.json()

      if (data.status === "ok") {
        this.fetchExisting()
        this.addPersonPanelTarget.classList.add("hidden")
        this.newPersonPanelTarget.classList.add("hidden")
      } else if (data.status === "duplicate") {
        this.showDuplicateAlert(data.existing_contact)
      } else {
        this.showError(data.message || "Failed to create contact.")
      }
    } catch (e) {
      this.showError("Failed to create contact.")
    }
  }

  showDuplicateAlert(contact) {
    this.duplicateAlertTarget.classList.remove("hidden")
    this.duplicateAlertTarget.innerHTML = `
      <div class="p-2 bg-[#E57373]/10 rounded-lg border border-[#E57373]/30">
        <p class="text-xs text-[#E57373] font-medium">Email already exists in PRT</p>
        <p class="text-[10px] text-[#2D2D2D] mt-1">${this.esc(contact.name)} &mdash; ${this.esc(contact.email)}</p>
        ${contact.outlet ? `<p class="text-[10px] text-[#8C8C8C]">${this.esc(contact.outlet)}</p>` : ''}
        <button type="button" data-action="click->people-popup#linkContact"
                data-contact-id="${contact.id}"
                class="mt-1.5 w-full py-1 text-xs rounded-md bg-[#8BA4B4] text-white hover:bg-[#7A93A3] transition-colors">
          Link this person instead
        </button>
      </div>
    `
  }

  showError(msg) {
    this.newPersonErrorTarget.classList.remove("hidden")
    this.newPersonErrorTarget.textContent = msg
  }

  clearMessages() {
    if (this.hasNewPersonErrorTarget) {
      this.newPersonErrorTarget.classList.add("hidden")
      this.newPersonErrorTarget.textContent = ""
    }
    if (this.hasDuplicateAlertTarget) this.duplicateAlertTarget.classList.add("hidden")
  }

  clearNewPersonForm() {
    this.newFirstNameTarget.value = ""
    this.newLastNameTarget.value = ""
    this.newEmailTarget.value = ""
    this.newRoleTarget.value = ""
    this.clearMessages()
  }

  emailStatusIcon(status, pendingCount) {
    switch (status) {
      case 'verified':
        return `<svg class="w-3.5 h-3.5 flex-shrink-0 text-[#9CAF88]" fill="currentColor" viewBox="0 0 20 20" title="Email verified"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>`
      case 'invalid':
        return `<svg class="w-3.5 h-3.5 flex-shrink-0 text-[#E57373]" fill="currentColor" viewBox="0 0 20 20" title="Email invalid"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>`
      case 'pending_verification':
        return `<svg class="w-3.5 h-3.5 flex-shrink-0 text-[#F5A623]" fill="none" stroke="currentColor" viewBox="0 0 24 24" title="Verification pending"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
      case 'candidates_found':
        return `<span class="inline-flex items-center gap-0.5 text-[#8BA4B4]" title="${pendingCount || ''} email candidate(s) found"><svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>${pendingCount ? `<span class="text-[10px] font-medium">${pendingCount}</span>` : ''}</span>`
      case 'not_found':
        return `<svg class="w-3.5 h-3.5 flex-shrink-0 text-[#8C8C8C]" fill="none" stroke="currentColor" viewBox="0 0 24 24" title="No email found"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/></svg>`
      default:
        return `<svg class="w-3.5 h-3.5 flex-shrink-0 text-[#D5D0C8]" fill="none" stroke="currentColor" viewBox="0 0 24 24" title="Email status unknown"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/></svg>`
    }
  }

  esc(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
